import { Box, Button, Dialog, Flex, Text, TextField, IconButton } from '@radix-ui/themes';
import { MdOutlineEdit } from 'react-icons/md';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../config/axiosConfig';
import { useFormik } from 'formik';
import { sensorSchema } from '../../Schema';
import DialogCloseButton from './DialogCloseButton';

const styles = `
  .input-error .rt-TextFieldInput {
    border-color: red;
  }
`;

const CreateSensor = ({ initialValues = null, onSensorCreated, showToast }) => {
  const isEditMode = !!initialValues;
  const [isOpen, setIsOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      sensorAddress: initialValues?.sensor_address || '',
      isin: initialValues?.isin || '',
      isvn: initialValues?.isvn || '',
    },
    validationSchema: sensorSchema, // Single schema for both create and edit
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const sensorData = {
          token: isEditMode ? initialValues.token : null,
          sensorAddress: values.sensorAddress,
          isin: values.isin,
          isvn: values.isvn,
          roomToken: null,
          institutionToken: null,
          timeOut: 0,
          isActive: true,
          isSensorOwned: false,
          isRegistered: false,
          isSuperAdmin: true,
          certificateThumbprint: null,
          certificateExpiration: null,
        };

        const endpoint = isEditMode ? '/sensor/update-sensor' : '/sensor/add-sensor';
        const response = await axiosInstance.post(endpoint, sensorData);

        if (response.data.result) {
          const successMessage = response.data.errorResponse.message;
          console.log(successMessage);
          if (showToast) showToast(successMessage);
          if (onSensorCreated) await onSensorCreated();
          setIsOpen(false);
          resetForm();
        } else {
          const errorMessage = response.data.errorResponse.message;
          console.error(errorMessage, response.data);
          if (showToast) showToast(errorMessage);
        }
      } catch (error) {
        const errorMessage = `Error ${isEditMode ? 'updating' : 'creating'} sensor`;
        console.error(errorMessage, error);
        if (showToast) showToast(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit, resetForm, setValues } = formik;

  // Update form values for edit mode
  useEffect(() => {
    if (isEditMode && isOpen && initialValues) {
      setValues({
        sensorAddress: initialValues.sensor_address || '',
        isin: initialValues.isin || '',
        isvn: initialValues.isvn || '',
      });
    }
  }, [isOpen, isEditMode, initialValues, setValues]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const submitForm = (e) => {
    e.preventDefault();
    formik.validateForm().then(errors => {
      if (Object.keys(errors).length === 0) {
        formik.handleSubmit();
      } else {
        Object.keys(formik.values).forEach(field => {
          formik.setFieldTouched(field, true);
        });
      }
    });
  };

  const dialogContent = isOpen ? (
    <Dialog.Content maxWidth="450px">
      <DialogCloseButton onClose={() => setIsOpen(false)} />
      <Dialog.Title>{isEditMode ? 'Manage Sensor Details' : 'Add Sensor'}</Dialog.Title>
      <Dialog.Description size="2" mb="4">
        {isEditMode ? 'Modify the sensor’s details.' : 'Fill in the details to add a new sensor.'}
      </Dialog.Description>

      <form onSubmit={handleSubmit}>
        <style>{styles}</style>
        <Flex direction="column" gap="3">
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">Sensor Address</Text>
            <TextField.Root
              name="sensorAddress"
              value={values.sensorAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter sensor address"
              disabled={isEditMode}
              className={errors.sensorAddress && touched.sensorAddress ? 'input-error' : ''}
            />
            {errors.sensorAddress && touched.sensorAddress && (
              <Text size="1" color="red" mt="1">{errors.sensorAddress}</Text>
            )}
          </label>
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">ISIN (Imacs Sensor Identification Number)</Text>
            <TextField.Root
              name="isin"
              value={values.isin}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter ISIN"
              className={errors.isin && touched.isin ? 'input-error' : ''}
            />
            {errors.isin && touched.isin && (
              <Text size="1" color="red" mt="1">{errors.isin}</Text>
            )}
          </label>
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">ISVN (Imacs Sensor Verification Number)</Text>
            <TextField.Root
              name="isvn"
              value={values.isvn}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter ISVN"
              className={errors.isvn && touched.isvn ? 'input-error' : ''}
            />
            {errors.isvn && touched.isvn && (
              <Text size="1" color="red" mt="1">{errors.isvn}</Text>
            )}
          </label>
        </Flex>
        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={() => setIsOpen(false)}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button type="button" disabled={isSubmitting} onClick={submitForm}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </Flex>
      </form>
    </Dialog.Content>
  ) : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      {isEditMode ? (
        <Dialog.Trigger>
          <IconButton variant="outline" onClick={() => setIsOpen(true)}>
            <MdOutlineEdit width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
      ) : (
        <Dialog.Trigger>
          <Button onClick={() => setIsOpen(true)}>Add Sensor</Button>
        </Dialog.Trigger>
      )}
      {isOpen && createPortal(dialogContent, document.body)}
    </Dialog.Root>
  );
};

export default CreateSensor;